import React from "react";
import SsidChartIcon from "@mui/icons-material/SsidChart";
import { MathJax } from "better-react-mathjax";

export const EXTRA_INFO_TEMPLATES = {
  "data-pre-loading": (
    <div>
      <b>Data Pre-loading:</b>
      <br />
      In the <b>Data Manager</b> you can load in your data. In the{" "}
      <b>File Queue</b> section, on the left side, you can select your files
      using the <b>File</b> or <b>Folder</b> button. The <b>Tutorial Data</b>{" "}
      button is only available during tutorials.
    </div>
  ),
  "data-loading-options": (
    <div>
      <b>Data Loading Options:</b>
      <br />
      You can set how your data should be interpreted. Select the{" "}
      <b>X-Data Units</b> of your data and the meaning of the different{" "}
      <b>Columns</b>. You can pre-cut or shift or scale you data. The result of
      the processing is displayed in the top section.
      <br />
      When satisfied, load your data with the <b>Apply</b> button. The{" "}
      <b>Apply All</b> button will process all files in the queue using the same
      options.
      <br />
     </div>
  ),
  "data-processing-window": (
    <div>
      <b>Data Processing Window:</b>
      <br />
      Clicking on the file name in the data list of the <b>Data</b> tab or in
      the graph legend, opens the <b>Data Processing</b> window. You can change
      the units of your data, and exclude data by changing the <b>Data Range</b>{" "}
      or by adding <b>Exclusion Ranges</b>. The number of remaining datapoints
      is listed.
      <br />
      Excluded data points are not displayed or taken into account for analysis
      and fitting, but can be reincluded by changing the exclusion options.
    </div>
  ),
  "graph-axis-range-settings": (
    <div>
      <b>Graph Settings:</b>
      <br />
      You can also change the axes by dragging the axes, or click on the
      autoscale button.
    </div>
  ),
  "graph-lines-display-options": (
    <div>
      <b>Graph Lines Display Options:</b>
      <br />
      You can change the color and line style of your displayed curves or remove
      them from the graph by clicking on the curve icon
      <SsidChartIcon
        style={{ fontSize: "1.2rem", verticalAlign: "middle" }}
      />{" "}
      and changing them in the displayed curves.
      <br />
      By clicking on the graph settings icon you can change the graph axes and
      labels.
    </div>
  ),
  "change-graph-line-colors-and-move-graph": (
    <div>
      <b>Multiple Graphs:</b>
      <br />
      You can move graphs to better organise your workspace.
      <br />
      For consistency, you might choose to change the graph line colors and
      styles to match between graphs. You can change the color and line style of
      your displayed curves or remove them from the graph by clicking on the
      curve icon{" "}
      <SsidChartIcon style={{ fontSize: "1.2rem", verticalAlign: "middle" }} />
      and changing them in the displayed curves.
    </div>
  ),
  "experiment-selection": (
    <div>
      <b>Experiment Selection:</b>
      <br />
      In the <b>Experiment</b> tab you can select the type of experiment. This
      creates one or more models describing the physics of your experiment. If
      you have any <b>app.speqqle</b> experiments saved you can load them
      directly using the <b>Load Experiment</b> button. To start a new
      experiment click on the <b>Add New Experiment</b> button. The available
      experiments are explained in more details in the <b>Manual</b>.
    </div>
  ),
  "managing-experiments-with-oscillators": (
    <div>
      <b>Managing Experiments with Oscillators:</b>
      <br />
      In the <b>Experiment</b> tab, you can manage your experiments. You can
      rename them, delete them, or if you have multiple experiments or models of
      the same type, you can link them, forcing them to become and stay
      identical. Linked models can always be unlinked.
      <br />
      Your model parameters that can be adjusted. These parameters represent
      physical properties of your sample or settings of your experiment. In the{" "}
      <b>Parameters</b> tab, you can view and modify these parameters. Try
      adjusting different values to see how they affect the model curve. You can
      add oscillators to your model by clicking on the <b>+</b> icon that
      appears when hovering on the right side of the model table.
    </div>
  ),
  "experiment-multilayer-bulk": (
    <div>
      <b>Isotropic R/T/A/Ellips model</b>
      <br />
      The <b>Isotropic R/T/A/Ellips</b> model can be used for either bulk or layered
      samples. By clicking on the green &quot;+&quot; symbol that appears next
      to the experiment name when you hover over it, you can add layers to your
      sample. For this experiment we are analysing a bulk sample so we do not
      need to add any layers.
    </div>
  ),
  "parameters-initialization": (
    <div>
      <b>Parameters Initialization:</b>
      <br />
      To find the best model parameters to describe your data, you can change
      the parameter values manually. Once you have achieved an approximate
      match, you can use the fit option in the <b>Fit</b> tab to optimize the
      values.
    </div>
  ),
  "changing-parameters": (
    <div>
      <b>Parameter Window:</b>
      <br />
      By left clicking on a parameter cell you open the parameter window. The
      slider allows you to see the effect this parameter has on the model curve.
      You can also set limits, fix the parameter, or group the parameters
      together. Fixed parameter are not changed by the fit, while grouped
      parameters are changed together.
    </div>
  ),
  "oscillators-out-of-range": (
    <div>
      <b>Oscillators Outside Data Range:</b>
      <br />
      Fitting oscillators at wavenumbers outside the data range is unphysical
      and will result in a poor fit. Make sure to only include oscillators in
      the fit that are within the data range. Sometimes the tail of an
      oscillator that peaks at the edge of the data range is necessary to
      describe the data. Try to fix as many parameters as possible of that
      oscillator.
    </div>
  ),
  "adding-oscillators": (
    <div>
      <b>Managing Oscillators:</b>
      <br />
      You can add as many oscillators as you want. The new oscillator will
      appear just below the line whose green &quot;+&quot; icon you clicked on.
      You can remove an oscillator by clicking on the red &quot;x&quot; icon.
      For material models where the order of the oscillators does not matter,
      you can easily reorder them based on one of the column parameters by left
      clicking on the column header. To change properties of an entire column at
      once, right click on the column header.
    </div>
  ),
  "parameters-movable-windows": (
    <div>
      <b>Workspace Organization:</b>
      <br />
      You can move the model windows to better organise your workspace. You can
      always go back to the default grid by clicking on the grid icon in the top
      right corner.
    </div>
  ),
  "lineshape-introduction": (
    <div>
      <b>Lineshapes:</b>
      <br />
      Some materials might be better represented by other lineshapes than the
      Drude-Lorentz oscillator. You add different lineshapes with the{" "}
      <b>Add Lineshape Type</b> button in the material model table.
    </div>
  ),
  "autofit-introduction": (
    <div>
      <b>AutoFit:</b>
      <br />
      To find the best model parameters to describe your data, you can change
      the parameter values manually. Once you have achieved an approximate
      match, you can use the fit option in the <b>Fit</b> tab to optimize the
      values.
      <br />
      For Raman the quicker way is to use the <b>AutoFit</b> function, which
      automatically finds the best initial parameters and performs a fit.
      <br />
      You can access the <b>AutoFit</b> function from the <b>AutoFit</b> button
      in a graph displaying a Raman model and data, or from any Raman model in
      the <b>Parameters</b> tab, in which case you need to select the data to
      fit your model.
    </div>
  ),
  "autofit-options": (
    <div>
      <b>AutoFit Options:</b>
      <br />
      You can chose the data that you want to use for the autofit, exclude data
      ranges manually and/or choose to let the software do the removal of the
      peak at zero frequency automatically. You can also provide a temperature
      estimate. If your data does not contain sufficient datapoints in both the
      negative and positive x-axis regions, a temperature estimate is necessary.
      <br />
      For the tutorial data the default settings are sufficient.
      <br />
      The <b>AutoFit</b> overwrites any existing model parameters or settings.
    </div>
  ),
  "fit-tab-introduction": (
    <div>
      <b>Fit Tab:</b>
      <br />
      The fit tab is where you define and run the fit. It has three main
      functions:
      <ol>
        <li>
          Creating the <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> terms.
        </li>
        <li>Choosing your fit options.</li>
        <li>Performing the fit.</li>
      </ol>{" "}
    </div>
  ),
  "chi2-term-introduction": (
    <div>
      <b>
        <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> Term:
      </b>
      <br />
      You can add as many <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> terms as
      you need. It is possible to have multiple{" "}
      <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> terms for the same model and
      different outputs. It is also possible to fit multiple models to the same
      dataset.{" "}
    </div>
  ),
  "weighted-chi2-terms-intro": (
    <div>
      <b>
        Weighted <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> Terms:
      </b>
      <br />
      You may want to adjust how strongly the fitting algorithm accounts for
      certain data sets. This can be achieved by modifying the weights of the
      <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> terms.
      <br />
      With equal weights, the fitting algorithm assigns the same importance to
      each data point. If one dataset is measured with higher precision, or if
      it has relatively few points but covers a critical parameter range, you
      may wish to increase its weight. This prevents large datasets from
      dominating the fit simply because they contain more points, ensuring that
      smaller but equally important datasets have an appropriate influence on
      the optimization.{" "}
    </div>
  ),
  "fit-options": (
    <div>
      <b>Fit Options:</b>
      <br />
      In most cases the default fit options are well-suited. In this case the
      fit will end when the <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> value
      changed less than 0.001 for the last 3 iterations, or the fit has done 40
      iteration or has run for more than 100 seconds. While the fit is running
      it will show updates on the
      <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> value, the model parameters
      and curves every 3 iterations. This will allow you to stop the fit if you
      see it moves towards an incorrect local minimum.
    </div>
  ),
  "fit-button-deactivated": (
    <div>
      <b>Fit Button:</b>
      <br />
      The <b>Fit</b> button is deactivated (grey) if there are no valid{" "}
      <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> terms. You can exclude{" "}
      <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> terms by clicking on
      it&apos;s blue area. A deactived term is white, and can be reactivated by
      clicking on it&apos;s white area again.
      <br />
      The <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> value is also considered
      deactivated if it has no free parameters (FP:0) or contains no data points
      (DP:0).{" "}
    </div>
  ),
  "undo-fit": (
    <div>
      <b>Undo Fit:</b>
      <br />
      You can always undo a fit by clicking on the <b>Undo Fit</b> button in the
      fit tab. You can try again after changing some parameter values, fixing
      some parameters or setting parameter limits.
    </div>
  ),
};

export const INSTRUCTIONS_TEMPLATES = {
  "possible-next-steps": (
    <div>
      <b>Possible Next Steps:</b>
      <br />
      <ol>
        <li>
          <b>Save and Load Sessions:</b> You can save your session at any time
          by clicking on the <b>Save</b> button in the top bar. A saved session
          can be loaded by clicking on the <b>Load</b> button.
        </li>
        <li>
          <b>Perform a regular fit:</b> If you are unsatisfied with the fit
          results, you can change any of the parameters manually, and then
          perform a manual fit in the <b>Fit</b> tab.
        </li>
        <li>
          <b>Export data:</b> You can export your model curves using the export
          button in the graph.
        </li>
      </ol>
    </div>
  ),
  "display-raman-model-in-existing-graph": (
    <div>
      <ol>
        <li>
          Optionally: close the <b>Experiments</b> tab, by clicking on the{" "}
          <b>Experiments</b> button in the top bar.
        </li>
        <li>
          Click on the curves icon{" "}
          <SsidChartIcon
            style={{ fontSize: "1.2rem", verticalAlign: "middle" }}
          />{" "}
          of the graph that contains the <b>Tutorial-Raman_Tutorial</b> data.
        </li>
        <li>
          Click on <b>Add Curves</b>.
        </li>
        <li>
          Select <b>Raman</b> from the <b>Add Model Curve</b> section on the right.
        </li>
        <li>Click anywhere outside the curve selection window to close.</li>
      </ol>
    </div>
  ),
  "display-sto-model-in-graph": (
    <div>
      <ol>
        <li>
          Optionally: close the <b>Experiments</b> tab, by clicking on the{" "}
          <b>Experiments</b> button in the top bar.
        </li>
        <li>
          Click on the curves icon{" "}
          <SsidChartIcon
            style={{ fontSize: "1.2rem", verticalAlign: "middle" }}
          />{" "}
          of the graph that contains the{" "}
          <b>STO-Reflectivity_Tutorial.txt</b> data.
        </li>
        <li>
          Click on <b>Add Curves</b>.
        </li>
        <li>
          Select <b>Bulk STO</b> from the <b>From Models:</b> section.
        </li>
        <li>
          Select the <b>Reflectivity</b> output.
        </li>
        <li>Click anywhere outside the curve selection window to close.</li>
      </ol>
    </div>
  ),
  "display-raman-data-in-graph": (
    <div>
      <ol>
        <li>
          Optionally: close the <b>Data</b> tab, by clicking on the <b>Data</b>{" "}
          button in the top bar.
        </li>
        <li>
          Click on the curves icon{" "}
          <SsidChartIcon
            style={{ fontSize: "1.2rem", verticalAlign: "middle" }}
          />{" "}
          of the graph.
        </li>
        <li>
          Click on <b>Add Curves</b>.
        </li>
        <li>
          Select <b>Raman_Tutorial</b> from the <b>Add Data Curve</b> section on the left.
        </li>
        <li>Click anywhere outside the curve selection window to close.</li>
      </ol>
    </div>
  ),
  "display-sto-data-in-graph-1": (
    <div>
      <ol>
        <li>
          Optionally: close the <b>Data</b> tab, by clicking on the <b>Data</b>{" "}
          button in the top bar.
        </li>
        <li>
          Click on the curves icon{" "}
          <SsidChartIcon
            style={{ fontSize: "1.2rem", verticalAlign: "middle" }}
          />{" "}
          of the graph.
        </li>
        <li>
          Click on <b>Add Curves</b>.
        </li>
        <li>
          Select <b>STO-Reflectivity_Tutorial.txt</b> from the{" "}
          <b>From Files:</b> section.
        </li>
        <li>Click anywhere outside the curve selection window to close.</li>
      </ol>
    </div>
  ),
  "display-sto-detector2-and-3-in-graph": (
    <div>
      <ol>
        <li>
          Optionally: close the <b>Data</b> tab, by clicking on the <b>Data</b>{" "}
          button in the top bar.
        </li>
        <li>
          Click on the curves icon{" "}
          <SsidChartIcon
            style={{ fontSize: "1.2rem", verticalAlign: "middle" }}
          />{" "}
          of the graph.
        </li>
        <li>
          Click on <b>Add Curves</b>.
        </li>
        <li>
          Select <b>STO-Detector2_Tutorial.dat</b> and{" "}
          <b>STO-Detector3_Tutorial.dat</b> from the <b>From Files:</b>{" "}
          section.
        </li>
        <li>Click anywhere outside the curve selection window to close.</li>
      </ol>
    </div>
  ),
  "add-second-sto-graph": (
    <div>
      <ol>
        <li>
          Click on the <b>Add Graph</b> button in the top bar.
        </li>
        <li>
          Select <b>STO-Reflectivity_Tutorial.txt</b> from the{" "}
          <b>From Files:</b> section.
        </li>
        <li>
          In the new graph, go to the curve menu{" "}
          <SsidChartIcon
            style={{ fontSize: "1.2rem", verticalAlign: "middle" }}
          />{" "}
          , click on <b>Add Curves</b> and add{" "}
          <b>STO-Detector3_Tutorial.dat</b>.
        </li>
        <li>
          Use to <b>Autoscale</b> function to zoom out.
        </li>
        <li>
          Add the <b>Bulk STO</b> model <b>Reflectivity</b> curve to the new
          graph.
        </li>
      </ol>
    </div>
  ),
  "initialize-sto-bulk-parameters": (
    <div>
      <ol>
        <li>
          Make sure the{" "}
          <b>
            <MathJax inline>{"\\( \\Theta \\)"}</MathJax>
          </b>{" "}
          and <b>pol</b> are 0 degrees.
        </li>
        <li>
          Right click the parameter to <b>fix</b> the value, it becomes bold if
          fixed. We know these values are correct, so we don&apos;t need to fit
          them.
        </li>
        <li>
          Make sure the sample contains only one layer (the lower table only has
          one row)
        </li>
        <li>
          Make sure the coherence <b>coh</b> is unchecked.
        </li>
        <li>
          Set the thickness (<b>d</b>) to 100 cm, in order for the sample to be
          bulk.
        </li>
        <li>
          Important: For any bulk sample, immediately <b>fix</b> the thickness
          by right clicking the parameter. It must become bold.
        </li>
      </ol>
    </div>
  ),
  "first-sto-oscillator-setup": (
    <div>
      <ol>
        <li>
          To create a <b>Drude</b> oscillator, change{" "}
          <b>
            <MathJax inline>{"\\( \\omega_0 \\)"}</MathJax>
          </b>{" "}
          of the first oscillator to 0 and fix it.
        </li>
        <li>
          Make the <b>Drude</b> oscillator stronger by increasing{" "}
          <b>
            <MathJax inline>{"\\( \\omega_p \\)"}</MathJax>
          </b>
          , try to recreate the wide plateau.
        </li>
        <li>
          For tutorial consistency set:
          <ol>
            <li>
              <b>
                <MathJax inline>{"\\( \\omega_0 \\)"}</MathJax>
              </b>{" "}
              to 0 cm<sup>-1</sup> and fixed,
            </li>
            <li>
              <b>
                <MathJax inline>{"\\( \\omega_p \\)"}</MathJax>
              </b>{" "}
              to 1700 cm<sup>-1</sup> and not fixed,
            </li>
            <li>
              <b>
                <MathJax inline>{"\\( \\gamma \\)"}</MathJax>
              </b>{" "}
              to 10 cm<sup>-1</sup> and not fixed.
            </li>
          </ol>
        </li>
      </ol>
    </div>
  ),
  "second-sto-oscillator-setup": (
    <div>
      <ol>
        <li>
          Change{" "}
          <b>
            <MathJax inline>{"\\( \\omega_0 \\)"}</MathJax>
          </b>{" "}
          of the second oscillator to move the peak,{" "}
          <b>
            <MathJax inline>{"\\( \\omega_p \\)"}</MathJax>
          </b>{" "}
          to adjust the strenght and{" "}
          <b>
            <MathJax inline>{"\\( \\gamma \\)"}</MathJax>
          </b>{" "}
          to adjust the width.
        </li>
        <li>
          Set the second oscillator to:
          <ol>
            <li>
              <b>
                <MathJax inline>{"\\( \\omega_0 \\)"}</MathJax>
              </b>
              = 550 cm<sup>-1</sup> and not fixed,
            </li>
            <li>
              <b>
                <MathJax inline>{"\\( \\omega_p \\)"}</MathJax>
              </b>{" "}
              = 700 cm<sup>-1</sup> and not fixed,
            </li>
            <li>
              <b>
                <MathJax inline>{"\\( \\gamma \\)"}</MathJax>
              </b>{" "}
              = 20 and not fixed.
            </li>
          </ol>
        </li>
      </ol>
    </div>
  ),
  "first-sto-oscillator-adjustment": (
    <div>
      <ol>
        <li>
          Change{" "}
          <b>
            <MathJax inline>{"\\( \\omega_0 \\)"}</MathJax>
          </b>{" "}
          of the first oscillator to 100 cm<sup>-1</sup>.
        </li>
        <li>
          Unfix{" "}
          <b>
            <MathJax inline>{"\\( \\omega_0 \\)"}</MathJax>
          </b>{" "}
          of the first oscillator.
        </li>
        <li>
          Set{" "}
          <b>
            <MathJax inline>{"\\( \\gamma \\)"}</MathJax>
          </b>{" "}
          of the first oscillator to 5 cm<sup>-1</sup>.
        </li>
      </ol>
    </div>
  ),
  "third-sto-oscillator-setup": (
    <div>
      <ol>
        <li>
          Increase the strenght of the oscillator{" "}
          <b>
            <MathJax inline>{"\\( \\omega_p \\)"}</MathJax>
          </b>{" "}
          until you start observing the effect (like to 500 cm<sup>-1</sup>).
        </li>
        <li>
          You could adapt{" "}
          <b>
            <MathJax inline>{"\\( \\omega_0 \\)"}</MathJax>
          </b>{" "}
          to 170 cm<sup>-1</sup>.
        </li>
        <li>
          The feature is very sharp, reduce{" "}
          <b>
            <MathJax inline>{"\\( \\gamma \\)"}</MathJax>
          </b>{" "}
          to 5 cm<sup>-1</sup>.
        </li>
        <li>
          To continue the tutorial, change the oscillator to these exact values.
        </li>
      </ol>
    </div>
  ),
  "data-tut-step3": (
    <div>
      <ol>
        <li>
          Make sure <b>Ellips_Tutorial.txt</b> is selected in the File Queue (appears in blue).
        </li>
        <li>
          In the <b>Raw Data</b> window you can see the file has a header and that the x units are cm<sup>-1</sup>. In <b>Data Loading options</b> make sure the X-Data Units is set to cm<sup>-1</sup>.
        </li>
        <li>
          In <b>Data Loading options</b> look for <b>Auto-Remove file header</b> and make sure it is checked.
        </li>
        <li>
          The data has 6 columns, we want to load columns 3 and 4, click on <b>LOAD ADDITIONAL COLUMN</b>.
        </li>
      </ol>
    </div>
  ),  

"data-tut-step6": (
    <div>
      <ol>
        <li>
          Check the <b>Processed Data</b> window.
        </li>
        <li>
          The data table should have three columns: x[cm<sup>-1</sup>], Psi and Delta. The first entries of these columns should be 50000, 22.63509 and 94.741867.
        </li>
        <li>
          If the header and the first line of the table are displayed correctly, click <b>APPLY</b>.
        </li>
      </ol>
    </div>
  ),  

  "data-tut-step8": (
    <div>
      <ol>
        <li>
          Check the <b>Processed Data</b> window. The table should have two columns: x[cm<sup>-1</sup>] and n. The first entries of these columns should be 23255.813953488374 and 2.580263665.
        </li>
        <li>
          If the column names and the first data points in the table are displayed correctly, click <b>APPLY</b>.
        </li>
      </ol>
    </div>
  ), 

  "data-tut-step9": (
    <div>
      <ol>
        <li>
          In the <b>Data Loading Options</b> change the <b>X-Data Unit</b> to cm<sup>-1</sup>.
        </li>
        <li>
          In the <b>Data Loading Options</b> remove the prefix or change it to <b>Ref</b>.
        </li>
        <li>
          Check the <b>Processed Data</b>: notice that the  <b>Ref</b> (or <b>y</b>) column has values larger then 1.
        </li>
        <li>
            In app.speqqle, reflectivity (as well as transmission or absorption) is expressed as a value between 0 and 1.  
            According to the dataset header, this file uses a different convention where reflectivity ranges from 0 to 100. To convert reflectivity: check <b>Shift or scale data points</b>, select <b>Y*Value</b> from the dropdown menu and enter 0.01 in the Value field.
        </li>
      </ol>
    </div>
  ),
 
   "thinfilm-tut-step3": (
    <div>
      <ol>
        <li>
          The data was measured in cm<sup>-1</sup> and formatted correctly. Therefore, there is no need to modify the <b>Data loading options</b>: simply click on the <b>Apply</b> or <b>Apply All</b> button to load the data.
        </li>
      </ol>
    </div>
  ),  

     "thinfilm-tut-step11": (
    <div>
      <ol>
        <li>
          Via the <b>Parameters</b> tab, open the parameters tables.
        </li>
        <li>
          <b>At this point, this tutorial window is blocking your view!</b> Hover in the top of this window and click-hold to <b>drag</b> this window to the left and drop it at a convenient location.
        </li>
        <li>
          Next, locate the table <b>sample 1</b> which contains the conditions of the experiment and the material stack.
        </li>
        <li>
          Set the angle of incidence (<b> <MathJax inline>{"\\( \\theta \\)"}</MathJax> </b>) to 8 and fix the value by right clicking on it.
        </li>
         <li>
          Each time you click in a parameter field, the parameter window will pop-up. If this is in your way, drag it to a better location on your screen.
        </li>
         <li>
          Fix the polarization (<b>pol</b>) by right clicking on it.
        </li>
        <li>
          Set the thickness of BK7 to 0.5 cm and fix that value, we will not fit it.
        </li>
      </ol>
    </div>
  ), 


  "thinfilm-tut-step13": (
    <div>
      <ol>
        <li>
          Open the Fit window via the <b>Fit</b> tab.
        </li>
          <li>
          Click <b>ADD <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> TERM</b>.
        </li>
        <li>
          From the file list (left), select <b>Coating1_Reflectivity_Tutorial.txt</b>.
        </li>
         <li>
          From the model list (right), select <b>sample 1</b> and then <b>Reflectivity</b>.
        </li>
         <li>
          Click <b>ADD <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> TERM</b>.
        </li>
         <li>
          Do the same for the transmission dataset: from the file list, select <b>Coating1_Transmission_Tutorial.txt</b>.
        </li>
        <li>
          From the model list (right), select <b>sample 1</b> and then <b>Transmission</b>.
        </li>
        <li>
          Click <b>ADD <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> TERM</b> and then <b>CLOSE</b>.
        </li>
      </ol>
    </div>
  ),  

    "thinfilm-tut-step18": (
    <div>
      <ol>
        <li>
          Open the <b>parameter tables</b> via the <b>Parameter tab</b> (in case you had closed it).
        </li>
          <li>
          Locate the table of <b>sample 2</b>.
        </li>
        <li>
          Hover next to the <b>Sample Layers</b> part of the table and click on the green plus sign to add a material layer.
        </li>
         <li>
          Set <b> <MathJax inline>{"\\( \\theta \\)"}</MathJax> </b> to 8 degrees and fix it.
        </li>
         <li>
          Keep  <b>pol</b> at 0 degrees and fix it.
        </li>
        <li>
          Left-click on the first (top) material in the <b>Sample Layers</b> list, from the pop-up window and select <b>Si3N4</b>.
        </li>
        <li>
          Left-click on the lower material in the <b>Sample Layers</b> list, from the pop-up window and select <b>BK7</b>.
        </li>
         <li>
          Set the thickness of BK7 to 0.5 cm and fix it.
        </li>
         <li>
          Set the thickness of Si<sup>3</sup>N<sup>4</sup> to 1e-5 cm (100nm), DO NOT fix it and check the <b>coh</b> box.        </li>
      </ol>
    </div>
  ),

   "thinfilm-tut-step20": (
    <div>
      <ol>
        <li>
          Open the Fit window via the <b>Fit</b> tab.
        </li>
          <li>
          Click <b>ADD <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> TERM</b>.
        </li>
        <li>
          From the file list (left), select <b>Coating2_Reflectivity_Tutorial.txt</b>.
        </li>
         <li>
          From the model list (right), select <b>sample 2</b> and then <b>Reflectivity</b>.
        </li>
         <li>
          Click <b>ADD <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> TERM</b>.
        </li>
         <li>
          Do the same for the transmission dataset: from the file list, select <b>Coating2_Transmission_Tutorial.txt</b>.
        </li>
        <li>
          From the model list (right), select <b>sample 2</b> and then <b>Transmission</b>.
        </li>
        <li>
          Click <b>ADD <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> TERM</b> and then <b>CLOSE</b>.
        </li>
      </ol>
    </div>
  ), 
  
     "thinfilm-tut-step24": (
    <div>
      <ol>
        <li>
          Open the Fit window via the <b>Fit</b> tab.
        </li>
          <li>
          Click <b>ADD <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> TERM</b>.
        </li>
        <li>
          From the file list (left), select <b>n Si3N4 2</b>.
        </li>
         <li>
          From the model list (right), select <b>Si3N4</b> and then <b>n_real</b>.
        </li>
         <li>
          Click <b>ADD <MathJax inline>{"\\( \\chi^2 \\)"}</MathJax> TERM</b>.
        </li>
      </ol>
    </div>
  ), 

       "thinfilm-tut-step25": (
    <div>
      <ol>
        <li>
          Look at the different oscillators of your Si3N4 parameter model.
        </li>
          <li>
          Most oscillators are far outside of the data range we have in this tutorial.
        </li>
        <li>
          Freeing to many parameters would make the fit unstable, but you can free <MathJax inline>{"\\( \\epsilon_\\inf \\)"}</MathJax>.
        </li>
         <li>
          You might also free <MathJax inline>{"\\( \\omega_p \\)"}</MathJax> and <MathJax inline>{"\\( \\gamma \\)"}</MathJax> of the oscillator at 907 cm<sup>-1</sup>. But not <MathJax inline>{"\\( \\omega_0 \\)"}</MathJax>, as we do not have data in that range in this tutorial.
        </li>
         <li>
          To allow the high energy part of n to match better, free the parameters of the Tauc Lorentz oscillator.
        </li>
           <li>
          Click <b>FIT</b>.
        </li>
      </ol>
    </div>
  ), 



};
